import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock, Users, Video, MapPin, Plus, Edit, Filter, ArrowLeft, Trash2 } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import {
  createSession,
  deleteSession,
  fetchAllPreferences,
  fetchSessions as loadSessions,
  updateSession,
} from '../../lib/instantData'
import { brandAlert, brandConfirm } from '../../contexts/DialogContext'
import GlassCard from '../../components/ui/GlassCard'
import PanelShell from '../../components/ui/PanelShell'
import DatePicker from '../../components/ui/DatePicker'


interface TrainingSession {
  id: string
  user_id: string
  trainer_id: string
  package_id: string
  scheduled_date: string
  duration_minutes: number
  session_type: 'in_person' | 'online' | 'meal_plan'
  status: 'scheduled' | 'completed' | 'cancelled'

  notes?: string
  client_name?: string
  client_email?: string
  package_name?: string
  // Meal plan specific fields
  meal_plan_weeks?: number
  pickup_delivery_info?: string
}

interface SessionData {
  id: string
  user_id: string
  trainer_id: string
  package_id: string
  scheduled_date: string
  duration_minutes: number
  session_type: 'in_person' | 'online' | 'meal_plan'
  status: 'scheduled' | 'completed' | 'cancelled'
  daily_room_id?: string
  notes?: string
  // Meal plan specific fields
  meal_plan_weeks?: number
  pickup_delivery_info?: string
  users?: {
    email?: string
    user_metadata?: {
      full_name?: string
    }
  }
  packages?: {
    name?: string
  }
}

const AdminSessions: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<TrainingSession[]>([])
  const [clients, setClients] = useState<Array<{id: string, name: string, email: string}>>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [selectedDate, setSelectedDate] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [sessionForm, setSessionForm] = useState({
    clientId: '',
    date: '',
    time: '',
    duration: 60,
    sessionType: '',
    notes: '',
    // Meal plan specific fields
    mealPlanWeeks: 4,
    pickupDeliveryInfo: ''
  })
  const [creatingSession, setCreatingSession] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingSession, setEditingSession] = useState<TrainingSession | null>(null)


  useEffect(() => {
    fetchSessions()
    fetchClients()
  }, [])

  const fetchSessions = async () => {
    try {
      const [sessionsData, preferences] = await Promise.all([
        loadSessions(),
        fetchAllPreferences(),
      ])

      const clientMap = new Map<string, { name: string; email: string }>()
      preferences.forEach((client) => {
        clientMap.set(client.user_id, {
          name: client.full_name || 'Unknown Client',
          email: client.email || 'unknown@email.com',
        })
      })

      const formattedSessions = sessionsData
        .slice()
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
        .map((session) => {
          const clientInfo = clientMap.get(session.user_id) || {
            name: session.client_full_name || 'Unknown Client',
            email: session.client_email || 'unknown@email.com',
          }
          return {
            ...session,
            trainer_id: session.trainer_id || 'admin',
            package_id: session.package_id || '',
            client_name: clientInfo.name,
            client_email: clientInfo.email,
            package_name: 'Manual Session',
          } as TrainingSession
        })

      setSessions(formattedSessions)
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      const preferences = await fetchAllPreferences()
      setClients(
        preferences.map((pref) => ({
          id: pref.user_id,
          name: pref.full_name || 'Unknown Client',
          email: pref.email || 'unknown@email.com',
        }))
      )
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const handleSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreatingSession(true)

    try {
      // Debug logging
      console.log('Session form data:', sessionForm)
      console.log('Validation check:', {
        clientId: !!sessionForm.clientId,
        date: !!sessionForm.date,
        time: !!sessionForm.time,
        sessionType: !!sessionForm.sessionType
      })

      // Validate required fields - different validation for meal plans
      const isMealPlan = sessionForm.sessionType === 'meal_plan'
      const requiredFieldsPresent = sessionForm.clientId && sessionForm.date && sessionForm.sessionType && 
        (isMealPlan || (sessionForm.time && sessionForm.duration))
      
      if (!requiredFieldsPresent) {
        console.log('Validation failed for:', {
          clientId: sessionForm.clientId,
          date: sessionForm.date,
          time: sessionForm.time,
          sessionType: sessionForm.sessionType,
          isMealPlan: isMealPlan
        })
        void brandAlert({ title: 'Something went wrong', message: 'Please fill in all required fields', variant: 'error' })
        setCreatingSession(false)
        return
      }

      // Combine date and time - use default time for meal plans
      const timeToUse = sessionForm.sessionType === 'meal_plan' ? '00:00' : sessionForm.time
      const scheduledDateTime = new Date(`${sessionForm.date}T${timeToUse}`).toISOString()

      // Create the session
      const durationToUse = sessionForm.sessionType === 'meal_plan' ? 0 : sessionForm.duration
      const client = clients.find((c) => c.id === sessionForm.clientId)
      await createSession({
        userId: sessionForm.clientId,
        trainerId: adminUser?.user_id || 'admin',
        scheduledDate: scheduledDateTime,
        durationMinutes: durationToUse,
        sessionType: sessionForm.sessionType,
        status: 'scheduled',
        notes: sessionForm.notes || undefined,
        mealPlanWeeks:
          sessionForm.sessionType === 'meal_plan' ? sessionForm.mealPlanWeeks : undefined,
        pickupDeliveryInfo:
          sessionForm.sessionType === 'meal_plan'
            ? sessionForm.pickupDeliveryInfo || undefined
            : undefined,
        clientFullName: client?.name,
        clientEmail: client?.email,
      })

      setSessionForm({
        clientId: '',
        date: '',
        time: '',
        duration: 60,
        sessionType: '',
        notes: '',
        mealPlanWeeks: 4,
        pickupDeliveryInfo: '',
      })
      setShowCreateModal(false)
      await fetchSessions()
      void brandAlert({ title: 'Done', message: 'Session scheduled successfully!', variant: 'success' })
    } catch (error) {
      console.error('Error creating session:', error)
      void brandAlert({ title: 'Something went wrong', message: 'An unexpected error occurred', variant: 'error' })
    } finally {
      setCreatingSession(false)
    }
  }

  const updateSessionStatus = async (sessionId: string, newStatus: string) => {
    try {
      await updateSession(sessionId, { status: newStatus })
      setSessions(
        sessions.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus as any } : session
        )
      )
    } catch (error) {
      console.error('Error updating session status:', error)
    }
  }

  const handleDeleteSession = async (session: TrainingSession) => {
    const when = new Date(session.scheduled_date).toLocaleString()
    const ok = await brandConfirm({
      title: 'Delete this session?',
      message: `${session.client_name || 'This client'}'s session on ${when} will be permanently removed and will disappear from their dashboard. This cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete session',
      cancelLabel: 'Keep session',
    })
    if (!ok) return

    try {
      await deleteSession(session.id)
      setSessions((prev) => prev.filter((s) => s.id !== session.id))
    } catch (error) {
      console.error('Error deleting session:', error)
      void brandAlert({ title: 'Something went wrong', message: 'Failed to delete the session. Please try again.', variant: 'error' })
    }
  }

  const handleEditSession = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingSession) return
    
    setCreatingSession(true)

    try {
      // Validate required fields - different validation for meal plans
      const isMealPlan = sessionForm.sessionType === 'meal_plan'
      const requiredFieldsPresent = sessionForm.clientId && sessionForm.date && sessionForm.sessionType && 
        (isMealPlan || (sessionForm.time && sessionForm.duration))
      
      if (!requiredFieldsPresent) {
        void brandAlert({ title: 'Something went wrong', message: 'Please fill in all required fields', variant: 'error' })
        setCreatingSession(false)
        return
      }

      // Combine date and time - use default time for meal plans
      const timeToUse = sessionForm.sessionType === 'meal_plan' ? '00:00' : sessionForm.time
      const scheduledDateTime = new Date(`${sessionForm.date}T${timeToUse}`).toISOString()

      // Update the session
      const durationToUse = sessionForm.sessionType === 'meal_plan' ? 0 : sessionForm.duration
      await updateSession(editingSession.id, {
        userId: sessionForm.clientId,
        scheduledDate: Date.parse(scheduledDateTime),
        durationMinutes: durationToUse,
        sessionType: sessionForm.sessionType,
        notes: sessionForm.notes || undefined,
        mealPlanWeeks:
          sessionForm.sessionType === 'meal_plan' ? sessionForm.mealPlanWeeks : undefined,
        pickupDeliveryInfo:
          sessionForm.sessionType === 'meal_plan'
            ? sessionForm.pickupDeliveryInfo || undefined
            : undefined,
      })

      setSessionForm({
        clientId: '',
        date: '',
        time: '',
        duration: 60,
        sessionType: '',
        notes: '',
        mealPlanWeeks: 4,
        pickupDeliveryInfo: '',
      })
      setShowEditModal(false)
      setEditingSession(null)
      await fetchSessions()
      void brandAlert({ title: 'Done', message: 'Session updated successfully!', variant: 'success' })
    } catch (error) {
      console.error('Error updating session:', error)
      void brandAlert({ title: 'Something went wrong', message: 'An unexpected error occurred', variant: 'error' })
    } finally {
      setCreatingSession(false)
    }
  }

  const filteredSessions = sessions.filter(session => {
    const matchesStatus = filterStatus === 'all' || session.status === filterStatus
    const matchesType = filterType === 'all' || session.session_type === filterType
    const matchesDate = !selectedDate || session.scheduled_date.startsWith(selectedDate)
    return matchesStatus && matchesType && matchesDate
  })

  // Separate active, completed, and cancelled sessions
  const activeSessions = filteredSessions.filter(session => session.status === 'scheduled')
  const completedSessions = filteredSessions.filter(session => session.status === 'completed')
  const cancelledSessions = filteredSessions.filter(session => session.status === 'cancelled')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'online':
        return <Video className="h-4 w-4" />
      case 'meal_plan':
        return <Calendar className="h-4 w-4" />
      default: // in_person
        return <MapPin className="h-4 w-4" />
    }
  }

  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gold"></div>
      </div>
    )
  }

  return (
    <div className="panel-page">
      <div className="panel-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="mb-4 flex items-center text-gray-600 hover:text-ink transition-colors"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Return to Admin Dashboard
              </button>
              <p className="eyebrow mb-3">Admin</p>
              <h1 className="panel-title flex items-center">
                <Calendar className="mr-3 h-8 w-8" />
                Session Management
              </h1>
              <p className="mt-2 text-gray-600">
                Schedule and manage all training sessions
              </p>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => {
                  // Reset form when opening modal
                  setSessionForm({
                    clientId: '',
                    date: '',
                    time: '',
                    duration: 60,
                    sessionType: '',
                    notes: '',
                    mealPlanWeeks: 4,
                    pickupDeliveryInfo: ''
                  })
                  setShowCreateModal(true)
                }}
                className="bg-ink text-white px-4 py-2 rounded-lg hover:bg-ink-soft transition-colors flex items-center"
              >
                <Plus className="mr-2 h-4 w-4" />
                Schedule Session
              </button>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
          <GlassCard className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                >
                  <option value="all">All Types</option>
                  <option value="online">Online</option>
                  <option value="in_person">Private</option>
                  <option value="meal_plan">Meal Plan</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <DatePicker
                  value={selectedDate}
                  onChange={setSelectedDate}
                  placeholder="mm/dd/yyyy"
                  className="w-full"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilterStatus('all')
                    setFilterType('all')
                    setSelectedDate('')
                  }}
                  className="btn-secondary w-full text-sm py-2.5"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Active Sessions List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlassCard className="p-6">
            <div className="mb-4">
              <h3 className="font-display text-lg font-semibold text-ink">Active Sessions</h3>
              <p className="text-gray-600 text-sm">Scheduled and completed sessions</p>
            </div>
            <div className="space-y-4">
              {activeSessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                                              <div className="flex-shrink-0">
                          {getTypeIcon(session.session_type)}
                        </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-ink">{session.client_name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(session.status)}`}>
                            {session.status}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <Calendar className="mr-1 h-4 w-4" />
                              {new Date(session.scheduled_date).toLocaleDateString()}
                            </span>
                            <span className="flex items-center">
                              <Clock className="mr-1 h-4 w-4" />
                              {new Date(session.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="flex items-center">
                              <Users className="mr-1 h-4 w-4" />
                              {session.package_name}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{session.client_email}</p>
                        {session.session_type === 'meal_plan' && (
                          <div className="mt-2 text-sm text-gray-600">
                            {session.meal_plan_weeks && (
                              <div className="mb-1">
                                <span className="font-medium">Duration:</span> {session.meal_plan_weeks} weeks
                              </div>
                            )}
                            {session.pickup_delivery_info && (
                              <div className="mb-1">
                                <span className="font-medium">Pickup/Delivery:</span> {session.pickup_delivery_info}
                              </div>
                            )}
                          </div>
                        )}
                        {session.notes && (
                          <p className="text-sm text-gray-600 mt-2 italic">"{session.notes}"</p>
                        )}
                      </div>
                    </div>
                                              <div className="flex items-center space-x-2">
                            {session.status === 'scheduled' && (
                        <>
                          <button
                            onClick={() => updateSessionStatus(session.id, 'completed')}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                          >
                            Mark Complete
                          </button>
                          <button
                            onClick={() => updateSessionStatus(session.id, 'cancelled')}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      <button 
                        onClick={() => {
                          setEditingSession(session)
                          setSessionForm({
                            clientId: session.user_id,
                            date: new Date(session.scheduled_date).toISOString().split('T')[0],
                            time: new Date(session.scheduled_date).toTimeString().slice(0, 5),
                            duration: session.duration_minutes,
                            sessionType: session.session_type,
                            notes: session.notes || '',
                            mealPlanWeeks: session.meal_plan_weeks || 4,
                            pickupDeliveryInfo: session.pickup_delivery_info || ''
                          })
                          setShowEditModal(true)
                        }}
                        className="p-2 text-gray-400 hover:text-brand transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSession(session)}
                        title="Delete session"
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
              {activeSessions.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No active sessions found matching your filters</p>
                </div>
              )}
            </div>
          </GlassCard>
        </motion.div>

        {/* Completed Sessions List */}
        {completedSessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8"
          >
            <GlassCard className="p-6">
              <div className="mb-4">
                <h3 className="font-display text-lg font-semibold text-ink">Completed Sessions</h3>
                <p className="text-gray-600 text-sm">Sessions that have been completed</p>
              </div>
              <div className="space-y-4">
                {completedSessions.map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="p-4 border border-green-200 rounded-lg bg-green-50/50 hover:border-green-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {session.session_type === 'online' ? (
                            <Video className="h-4 w-4 text-brand" />
                          ) : session.session_type === 'meal_plan' ? (
                            <Calendar className="h-4 w-4 text-brand" />
                          ) : (
                            <MapPin className="h-4 w-4 text-brand" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="font-semibold text-ink">{session.client_name}</h3>
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            <div className="flex items-center space-x-4">
                              <span className="flex items-center">
                                <Calendar className="mr-1 h-4 w-4" />
                                {new Date(session.scheduled_date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center">
                                <Clock className="mr-1 h-4 w-4" />
                                {new Date(session.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="flex items-center">
                                <Users className="mr-1 h-4 w-4" />
                                {session.package_name}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{session.client_email}</p>
                          {session.session_type === 'meal_plan' && (
                            <div className="mt-2 text-sm text-gray-600">
                              {session.meal_plan_weeks && (
                                <div className="mb-1">
                                  <span className="font-medium">Duration:</span> {session.meal_plan_weeks} weeks
                                </div>
                              )}
                              {session.pickup_delivery_info && (
                                <div className="mb-1">
                                  <span className="font-medium">Pickup/Delivery:</span> {session.pickup_delivery_info}
                                </div>
                              )}
                            </div>
                          )}
                          {session.notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">"{session.notes}"</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            setEditingSession(session)
                            setSessionForm({
                              clientId: session.user_id,
                              date: new Date(session.scheduled_date).toISOString().split('T')[0],
                              time: new Date(session.scheduled_date).toTimeString().slice(0, 5),
                              duration: session.duration_minutes,
                              sessionType: session.session_type,
                              notes: session.notes || '',
                              mealPlanWeeks: session.meal_plan_weeks || 4,
                              pickupDeliveryInfo: session.pickup_delivery_info || ''
                            })
                            setShowEditModal(true)
                          }}
                          className="p-2 text-gray-400 hover:text-brand transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session)}
                          title="Delete session"
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        {/* Cancelled Sessions List */}
        {cancelledSessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8"
          >
            <GlassCard className="p-6">
              <div className="mb-4">
                <h3 className="font-display text-lg font-semibold text-ink">Cancelled Sessions</h3>
                <p className="text-gray-600 text-sm">Sessions that have been cancelled</p>
              </div>
              <div className="space-y-4">
                {cancelledSessions.map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="p-4 border border-red-200 rounded-lg bg-red-50/50 hover:border-red-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {session.session_type === 'online' ? (
                            <Video className="h-4 w-4 text-red-500" />
                          ) : session.session_type === 'meal_plan' ? (
                            <Calendar className="h-4 w-4 text-red-500" />
                          ) : (
                            <MapPin className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="font-semibold text-gray-500 line-through">{session.client_name}</h3>
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                              Cancelled
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            <div className="flex items-center space-x-4">
                              <span className="flex items-center">
                                <Calendar className="mr-1 h-4 w-4" />
                                {new Date(session.scheduled_date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center">
                                <Clock className="mr-1 h-4 w-4" />
                                {new Date(session.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="flex items-center">
                                <Users className="mr-1 h-4 w-4" />
                                {session.package_name}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{session.client_email}</p>
                          {session.session_type === 'meal_plan' && (
                            <div className="mt-2 text-sm text-gray-600">
                              {session.meal_plan_weeks && (
                                <div className="mb-1">
                                  <span className="font-medium">Duration:</span> {session.meal_plan_weeks} weeks
                                </div>
                              )}
                              {session.pickup_delivery_info && (
                                <div className="mb-1">
                                  <span className="font-medium">Pickup/Delivery:</span> {session.pickup_delivery_info}
                                </div>
                              )}
                            </div>
                          )}
                          {session.notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">"{session.notes}"</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            setEditingSession(session)
                            setSessionForm({
                              clientId: session.user_id,
                              date: new Date(session.scheduled_date).toISOString().split('T')[0],
                              time: new Date(session.scheduled_date).toTimeString().slice(0, 5),
                              duration: session.duration_minutes,
                              sessionType: session.session_type,
                              notes: session.notes || '',
                              mealPlanWeeks: session.meal_plan_weeks || 4,
                              pickupDeliveryInfo: session.pickup_delivery_info || ''
                            })
                            setShowEditModal(true)
                          }}
                          className="p-2 text-gray-400 hover:text-brand transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session)}
                          title="Delete session"
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}

        {/* Schedule Session Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-ink mb-4">Schedule New Session</h2>
                
                <form onSubmit={handleSessionSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                      <select
                        required
                        value={sessionForm.clientId}
                        onChange={(e) => setSessionForm({...sessionForm, clientId: e.target.value})}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                      >
                        <option value="">Select a client</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name} ({client.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                      <select
                        required
                        value={sessionForm.sessionType}
                        onChange={(e) => setSessionForm({...sessionForm, sessionType: e.target.value})}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                      >
                        <option value="">Select type</option>
                        <option value="in_person">In-Person</option>
                        <option value="online">Online</option>
                        <option value="meal_plan">Meal Plan</option>
                      </select>
                    </div>
                  </div>
                  
                  {sessionForm.sessionType === 'meal_plan' ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <DatePicker
                          value={sessionForm.date}
                          onChange={(date) => setSessionForm({...sessionForm, date})}
                          placeholder="mm/dd/yyyy"
                          className="w-full"
                          required
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                          <DatePicker
                            value={sessionForm.date}
                            onChange={(date) => setSessionForm({...sessionForm, date})}
                            placeholder="mm/dd/yyyy"
                            className="w-full"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                          <input
                            type="time"
                            required
                            value={sessionForm.time}
                            onChange={(e) => setSessionForm({...sessionForm, time: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                          <input
                            type="number"
                            required
                            min="30"
                            max="180"
                            step="15"
                            value={sessionForm.duration}
                            onChange={(e) => setSessionForm({...sessionForm, duration: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Meal Plan Specific Fields */}
                  {sessionForm.sessionType === 'meal_plan' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Weeks</label>
                        <select
                          value={sessionForm.mealPlanWeeks}
                          onChange={(e) => setSessionForm({...sessionForm, mealPlanWeeks: parseInt(e.target.value)})}
                          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                        >
                          <option value={2}>2 Weeks</option>
                          <option value={4}>4 Weeks</option>
                          <option value={6}>6 Weeks</option>
                          <option value={8}>8 Weeks</option>
                          <option value={12}>12 Weeks</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pickup/Delivery Time</label>
                        <input
                          type="text"
                          value={sessionForm.pickupDeliveryInfo}
                          onChange={(e) => setSessionForm({...sessionForm, pickupDeliveryInfo: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          placeholder="e.g., Daily at 6PM, Weekly pickup on Sundays"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                    <textarea
                      rows={3}
                      value={sessionForm.notes}
                      onChange={(e) => setSessionForm({...sessionForm, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Any special notes or instructions for this session"
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="btn-secondary text-sm px-5 py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creatingSession}
                      className="btn-gold text-sm px-5 py-2.5 rounded-full hover:bg-ink-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingSession ? 'Scheduling...' : 'Schedule Session'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Session Modal */}
        {showEditModal && editingSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-ink mb-4">Edit Session</h2>
                
                <form onSubmit={handleEditSession} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                      <select
                        required
                        value={sessionForm.clientId}
                        onChange={(e) => setSessionForm({...sessionForm, clientId: e.target.value})}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                      >
                        <option value="">Select a client</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name} ({client.email})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                      <select
                        required
                        value={sessionForm.sessionType}
                        onChange={(e) => setSessionForm({...sessionForm, sessionType: e.target.value})}
                        className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                      >
                        <option value="">Select type</option>
                        <option value="in_person">In-Person</option>
                        <option value="online">Online</option>
                        <option value="meal_plan">Meal Plan</option>
                      </select>
                    </div>
                  </div>
                  
                  {sessionForm.sessionType === 'meal_plan' ? (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <DatePicker
                          value={sessionForm.date}
                          onChange={(date) => setSessionForm({...sessionForm, date})}
                          placeholder="mm/dd/yyyy"
                          className="w-full"
                          required
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                          <DatePicker
                            value={sessionForm.date}
                            onChange={(date) => setSessionForm({...sessionForm, date})}
                            placeholder="mm/dd/yyyy"
                            className="w-full"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                          <input
                            type="time"
                            required
                            value={sessionForm.time}
                            onChange={(e) => setSessionForm({...sessionForm, time: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                          <input
                            type="number"
                            required
                            min="30"
                            max="180"
                            step="15"
                            value={sessionForm.duration}
                            onChange={(e) => setSessionForm({...sessionForm, duration: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Meal Plan Specific Fields */}
                  {sessionForm.sessionType === 'meal_plan' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Weeks</label>
                        <select
                          value={sessionForm.mealPlanWeeks}
                          onChange={(e) => setSessionForm({...sessionForm, mealPlanWeeks: parseInt(e.target.value)})}
                          className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent appearance-none"
                        >
                          <option value={2}>2 Weeks</option>
                          <option value={4}>4 Weeks</option>
                          <option value={6}>6 Weeks</option>
                          <option value={8}>8 Weeks</option>
                          <option value={12}>12 Weeks</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pickup/Delivery Time</label>
                        <input
                          type="text"
                          value={sessionForm.pickupDeliveryInfo}
                          onChange={(e) => setSessionForm({...sessionForm, pickupDeliveryInfo: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                          placeholder="e.g., Daily at 6PM, Weekly pickup on Sundays"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                    <textarea
                      rows={3}
                      value={sessionForm.notes}
                      onChange={(e) => setSessionForm({...sessionForm, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Any special notes or instructions for this session"
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditModal(false)
                        setEditingSession(null)
                      }}
                      className="btn-secondary text-sm px-5 py-2.5"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creatingSession}
                      className="btn-gold text-sm px-5 py-2.5 rounded-full hover:bg-ink-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingSession ? 'Updating...' : 'Update Session'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  )
}

export default AdminSessions 